// Batches — live on templateID 3, the SAME table courseService.ts calls
// "Courses". This was originally built against a self-invented templateID 20
// ("Batches"), which turned out to be one of this migration's unregistered
// templates (zero real fields ever saved) — a live, site-wide bug: every
// course's "select a batch" step queried an always-empty table, so nobody
// could book anything. Investigation (2026-07-24) found the real batch data
// (dates, serial numbers, fees, capacity) was in templateID 3 the whole
// time: it has 180 real rows but only ~26 distinct course names — each row
// is one scheduled BATCH (e.g. "MOI-06" 06–26 Nov 2023, "MOI-07" 01–21 Nov
// 2024, ...), not a course definition. See docs/BACKEND_SPEC_MISSING_ENDPOINTS.md.
//
// templateID 3 has no real "courseSlug" field, so batches are looked up by
// the real "Category Name" (broad category, e.g. "Mountaineering" — spans
// several course names, used by the public booking flow's tabbed selector)
// or "Course Name" (one specific course, used by the admin Courses page's
// cascading-delete lookup) via server-side filters, using the slug maps
// below. Booked-seat counts still come from the /batches/bookingCounts
// aggregation endpoint (P2 spec) rather than reading Bookings directly.
import {
  addUpdateTemplateDataWeb, bulkImportTemplateData, cell, deleteTemplateData,
  getMaxRecordidInTemplate, getTemplateDataList, isApiConnected, type TemplateRow,
} from "@/lib/apiClient";
import { normalizeOldAppDate } from "@/lib/oldAppDate";

const BATCHES_TEMPLATE_ID = 3;

// Read positions match courseService.ts's SCHEMA exactly — both services
// read/write the same real templateID-3 rows and must agree on field layout.
const SCHEMA = {
  id: 0,
  sr: 3,
  categoryName: 4,
  courseName: 5,
  duration: 6,
  seats: 7,
  season: 8,
  serialNo: 9,
  datesFrom: 10,
  datesTo: 11,
  fees: 12,
  capacity: 13,
  age: 14,
  type: 15,
  availableSeats: 16,
} as const;

// Parent/category-level slug -> real "Category Name" value. Confirmed live
// (2026-07-24): kayaking courses are filed under "White Water Rafting" in
// the real data, not a separate "Kayaking" category; "sports-climbing" is
// filed under "Mountaineering". Real values have inconsistent trailing
// spaces ("Paragliding " vs "Paragliding") — matched case/space-insensitively.
const SLUG_TO_CATEGORY: Record<string, string> = {
  mountaineering: "Mountaineering",
  "sports-climbing": "Mountaineering",
  "mountain-terrain-biking": "Mountain Terrain Biking",
  "scuba-diving": "SCUBA Diving",
  "white-water-rafting": "White Water Rafting",
  kayaking: "White Water Rafting",
  paragliding: "Paragliding",
  paramotor: "Paramotor",
};

// Specific sub-course slug -> real "Course Name" value (confirmed live
// 2026-07-24 — these differ significantly from the guesses baked into the
// old templateID-20 design; e.g. "sports-climbing" is really
// "Sport Climbing Course (SCC)", not a generic "SP" prefix).
export const SLUG_TO_COURSE_NAME: Record<string, string> = {
  "basic-mountaineering-course": "Basic Mountaineering Course (BMC)",
  "advanced-mountaineering-course": "Advance Mountaineering Course (AMC)",
  "search-rescue-mountaineering": "Search and Rescue Course (S&R)",
  "method-of-instruction-mountaineering": "Method of Instruction Course (MOI)",
  "sports-climbing": "Sport Climbing Course (SCC)",
  "basic-mountain-terrain-biking": "Basic Mountain Terrain Biking Course (BMTB)",
  "advanced-mountain-terrain-biking": "Advance Mountain Terrain Biking Course (AMTB)",
  "scuba-diving-basic": "Basic Scuba Diving Course (BSDC)",
  "scuba-diving-advanced": "Advance Scuba Diving Course (ASDC)",
  "search-rescue-scuba": "Search and Rescue Scuba Diving Course (S&RSDC)",
  "scuba-combined": "Combined (Basic, Advance & Rescue) Scuba Diving Course (CSDC)",
  "white-water-rafting-basic": "Basic White Water Rafting Course (BWWR)",
  "white-water-rafting-intermediate": "Intermediate White Water Rafting Course (IWWR)",
  "kayaking-basic": "Basic/Beginner White Water Kayaking Course (BWWKC)",
  "paragliding-basic-intermediate": "Basic and Intermediate Paragliding Course (P1&P2 Level) (BIPG)",
  "paragliding-advanced": "Advance Paragliding Course (P3 Level) (APGC)",
  "paragliding-combined": "Combined Paragliding Course (P1,P2 &P3 Level) (CPGC)",
  "paragliding-refresher": "Paragliding Refresher Course",
  "paramotor-basic": "Basic Paramotor Course (BPMC)",
  "paramotor-intermediate": "Intermediate Paramotor Course (IPMC)",
  "paramotor-trike": "Paramotor Trike Course (PMTC)",
};

// Course Name -> real Category Name, derived from the slug maps above.
const COURSE_NAME_TO_CATEGORY: Record<string, string> = Object.fromEntries(
  Object.entries(SLUG_TO_COURSE_NAME).map(([slug, name]) => [
    name,
    SLUG_TO_CATEGORY[slug] ?? "",
  ]),
);

// Reverse of SLUG_TO_COURSE_NAME — real Course Name -> slug, used to route a
// Bookings row (which only stores the course name, not a slug) back to its
// booking-flow URL (e.g. "Pay Now" on /student/bookings).
const COURSE_NAME_TO_SLUG: Record<string, string> = Object.fromEntries(
  Object.entries(SLUG_TO_COURSE_NAME).map(([slug, name]) => [name, slug]),
);

export function slugForCourseName(courseName: string): string | null {
  return COURSE_NAME_TO_SLUG[courseName?.trim()] ?? null;
}

export interface BatchRow {
  id: string;
  /** Row's own Sr (_1) value — must be threaded back through on every edit
   *  (see toWriteFields) since an update always explicitly writes _1, and an
   *  empty value here would blank out the stored Sr on save. */
  sr: string;
  courseSlug: string;
  courseName: string;
  category: string;
  courseCode: string;
  startDate: string;
  endDate: string;
  capacity: number;
  /** The batch's own stored seat counter (templateID 3, _14) — ground truth,
   *  confirmed live 2026-08-20 to be exactly what the production site
   *  displays. Booked seats should always be derived from this
   *  (capacity - availableSeats), not recomputed independently from
   *  Bookings — an earlier version of this app did that and produced
   *  numbers that didn't match the live reference site. */
  availableSeats: number;
  fees: number;
  ageRange: string;
  type: string;
  status: string;
  instructor: string;
  location: string;
}

// Distinct real Category Name values, confirmed live against templateID 3
// (2026-07-24) — trimmed, since real data has inconsistent trailing spaces
// ("Paragliding " vs "Paragliding"). Used for the admin Batches category
// filter; kept as a fixed list rather than derived from the current page's
// rows so the filter doesn't shrink/change depending on what's loaded.
export const BATCH_CATEGORIES = [
  "Mountaineering",
  "Mountain Terrain Biking",
  "Paragliding",
  "Paramotor",
  "SCUBA Diving",
  "White Water Rafting",
] as const;

// Real dates come back as "16-Mar-2027"; the app's own writes use
// "YYYY-MM-DD" — normalizeOldAppDate handles both shapes without going
// through Date()'s timezone-sensitive parsing (see lib/oldAppDate.ts).
const parseOldAppDate = normalizeOldAppDate;

function transformRow(row: TemplateRow[], courseSlug: string): BatchRow {
  const capacity = Number(cell(row, SCHEMA.capacity) || 0);
  const availableSeats = Number(cell(row, SCHEMA.availableSeats) || 0);
  return {
    id: cell(row, SCHEMA.id),
    sr: cell(row, SCHEMA.sr),
    courseSlug,
    courseName: cell(row, SCHEMA.courseName),
    category: cell(row, SCHEMA.categoryName).trim(),
    courseCode: cell(row, SCHEMA.serialNo),
    startDate: parseOldAppDate(cell(row, SCHEMA.datesFrom)),
    endDate: parseOldAppDate(cell(row, SCHEMA.datesTo)),
    capacity,
    availableSeats,
    fees: Number(cell(row, SCHEMA.fees) || 0),
    ageRange: cell(row, SCHEMA.age),
    type: cell(row, SCHEMA.type) || "Mixed",
    // No real per-batch "status" field on templateID 3 — derived from
    // availableSeats instead (see updateBatchStatus).
    status: availableSeats > 0 ? "open" : "full",
    // No real instructor/location fields on templateID 3 — nothing to read.
    instructor: "",
    location: "",
  };
}

export async function listBatchesForCourse(courseSlug: string): Promise<BatchRow[]> {
  if (!isApiConnected() || !courseSlug) return [];

  const courseName = SLUG_TO_COURSE_NAME[courseSlug];
  const category = courseName ? COURSE_NAME_TO_CATEGORY[courseName] : SLUG_TO_CATEGORY[courseSlug];
  if (!category) return [];

  // Filter by Category Name only — confirmed live (2026-07-24) that
  // filtering by "Course Name" with a value containing parentheses (which
  // most real course names have, e.g. "Sport Climbing Course (SCC)")
  // silently returns zero rows, while the same query without parens works.
  // The backend appears to treat the filter value as a pattern where "(" /
  // ")" break the match. Category Name never has this problem (no special
  // characters), so filter on that and do the exact course-name match
  // client-side instead of relying on the backend for it.
  const { response } = await getTemplateDataList(BATCHES_TEMPLATE_ID, { "Category Name": category }, { pgSize: 500 });
  const rows = (response.records || []).map((r) => transformRow(r, courseSlug));
  return courseName ? rows.filter((r) => r.courseName === courseName) : rows;
}

// Fresh (uncached) read of one batch's own stored AvailableSeats counter —
// for the moment right before committing to a booking, when a value that's
// even a few seconds stale risks overbooking. "Serial No" has no
// parentheses so it's safe to filter on directly (unlike "Course Name" —
// see the parenthesis-filter quirk documented on listBatchesForCourse).
export async function getLatestAvailableSeats(serialNo: string): Promise<number | null> {
  if (!isApiConnected() || !serialNo) return null;
  const { response } = await getTemplateDataList(BATCHES_TEMPLATE_ID, { "Serial No": serialNo }, { pgSize: 1 });
  const row = response.records?.[0];
  return row ? Number(cell(row, SCHEMA.availableSeats) || 0) : null;
}

// Decrements a batch's stored AvailableSeats counter after a booking is
// created through this app, so the field stays accurate for whoever reads
// it next (including the legacy production site, which trusts this same
// field). currentAvailable should come from a just-fetched value (e.g.
// getLatestAvailableSeats), not a stale one, to keep this close to atomic.
export async function decrementAvailableSeats(batchId: string, seatsBooked: number, currentAvailable: number): Promise<void> {
  if (!batchId || seatsBooked <= 0) return;
  const next = Math.max(0, currentAvailable - seatsBooked);
  await addUpdateTemplateDataWeb(BATCHES_TEMPLATE_ID, { _14: next }, batchId);
}

// ─── Admin CRUD ───

export interface AdminBatchListResult {
  batches: BatchRow[];
  totalCount: number;
}

export async function listAllBatches(page: number, pageSize: number, category?: string): Promise<AdminBatchListResult> {
  if (!isApiConnected()) return { batches: [], totalCount: 0 };
  const filters = category && category !== "All" ? { "Category Name": category } : {};
  const { response } = await getTemplateDataList(BATCHES_TEMPLATE_ID, filters, { index: page, pgSize: pageSize });
  const rows = (response.records || []).map((r) => transformRow(r, ""));
  return { batches: rows, totalCount: response.totalRecords ?? response.count ?? rows.length };
}

export interface BatchWrite {
  courseSlug: string;
  courseName: string;
  courseCode: string;
  startDate: string;
  endDate: string;
  capacity: number;
  fees: number;
  ageRange: string;
  type: string;
  status: string;
  instructor: string;
  location: string;
  // Preserves the row's existing Sr value across an edit (avoids the
  // field-shift-on-create bug — see courseService.ts's SCHEMA comment for
  // the full explanation); omitted only when creating a fresh row.
  sr?: string;
}

function durationLabel(startDate: string, endDate: string): string {
  const from = new Date(startDate).getTime();
  const to = new Date(endDate).getTime();
  if (isNaN(from) || isNaN(to) || to < from) return "";
  const days = Math.round((to - from) / 86400000) + 1;
  return `${days} Day${days === 1 ? "" : "s"}`;
}

// templateID 3 requires every field up to _14 (AvailableSeats) on create —
// omitting a registered field doesn't store it blank, it drops the array
// slot entirely and shifts every later field left by one (confirmed live,
// see courseService.ts). There's no real per-batch status/instructor/
// location field, so those aren't written anywhere.
function toWriteFields(b: BatchWrite): Record<string, unknown> {
  return {
    _1: b.sr ?? "",
    _2: COURSE_NAME_TO_CATEGORY[b.courseName] ?? "",
    _3: b.courseName,
    _4: durationLabel(b.startDate, b.endDate),
    _5: b.capacity,
    _6: "All Season",
    _7: b.courseCode,
    _8: b.startDate,
    _9: b.endDate,
    _10: b.fees,
    _11: b.capacity,
    _12: b.ageRange,
    _13: b.type,
  };
}

export async function createBatch(batch: BatchWrite): Promise<void> {
  const { response: maxIdResp } = await getMaxRecordidInTemplate(BATCHES_TEMPLATE_ID);
  const sr = String(maxIdResp?.maxid ?? 1);
  // _14 (AvailableSeats) is only set on create, initialized to full capacity
  // — never overwritten on update, matching Courses' same field (see
  // courseService.ts's createCourse comment).
  await addUpdateTemplateDataWeb(BATCHES_TEMPLATE_ID, { ...toWriteFields({ ...batch, sr }), _14: batch.capacity });
}

export async function updateBatch(id: string, batch: BatchWrite): Promise<void> {
  await addUpdateTemplateDataWeb(BATCHES_TEMPLATE_ID, toWriteFields(batch), id);
}

// No real per-batch status field — "full" is represented by zeroing out
// AvailableSeats (_14) rather than a status flag; "open" restores it to the
// batch's capacity.
export async function updateBatchStatus(id: string, status: string, capacity: number): Promise<void> {
  await addUpdateTemplateDataWeb(BATCHES_TEMPLATE_ID, { _14: status === "open" ? capacity : 0 }, id);
}

export async function deleteBatches(ids: string[]): Promise<void> {
  await deleteTemplateData(BATCHES_TEMPLATE_ID, ids);
}

export async function bulkImportBatches(rows: BatchWrite[]): Promise<{ insertedCount: number; updatedCount: number; skippedCount: number }> {
  const { response } = await bulkImportTemplateData(
    BATCHES_TEMPLATE_ID,
    rows.map(toWriteFields),
    { onConflictField: "_7", upsert: false },
  );
  return response;
}
